import React, { useRef, useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import CreateLayout from '@/shared/layout/createLayout';
import { TASK_FORM_LABELS } from '../../constants/labels';
import { TASK_FORM_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import { useEditForm } from '../../hooks/useEditForm';
import { FormBuilder } from '../createForm/CreateForm';
import FormPreview from '../createForm/FormPreview';

// ─── Page header ──────────────────────────────────────────────────────────────

const FormPageHeader = () => (
  <p className="text-titleLarge text-neutral-10">{TL.EDIT_TITLE.polish}</p>
);

// ─── EditForm ─────────────────────────────────────────────────────────────────

const EditForm: React.FC<{ id: string }> = ({ id }) => {
  const {
    loading,
    formId,
    notFound,
    loadError,
    formState,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setName,
    setDescription,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleOpenPreview,
    handleClosePreview,
    handleCloseErrorSnackbar,
  } = useEditForm(id);

  const sectionDragIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (validationAttemptCount === 0) {
      return;
    }
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-error-field="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [validationAttemptCount]);

  const handlePreviewSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleOpenPreview();
    },
    [handleOpenPreview]
  );

  const handleSaveSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleSaveFromPreview();
    },
    [handleSaveFromPreview]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (loadError || !formState) {
    return (
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4">
        <p className="text-sm text-[#E11B1B]">
          {loadError || TASK_FORM_LABELS.EDIT_ERROR_GENERIC}
        </p>
      </div>
    );
  }

  if (isPreviewOpen) {
    return (
      <CreateLayout
        key="preview"
        isSubmitting={isSubmitting}
        handleSubmit={handleSaveSubmit}
        handleCancel={handleClosePreview}
        isCancelModalOpen={false}
        handleCloseCancelModal={() => {}}
        handleConfirmCancel={() => {}}
        isSnackbarOpen={isErrorSnackbarOpen}
        handleCloseSnackbar={handleCloseErrorSnackbar}
        snackbarError={snackbarError}
        cancelButtonLabel={TASK_FORM_LABELS.BUTTON_BACK_TO_EDIT}
        submitButtonLabel={TASK_FORM_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={TASK_FORM_LABELS.BUTTON_SAVING}
        Header={FormPageHeader}
        headerClassName="mb-5 py-2"
      >
        <FormPreview formState={formState} isSubmitting={isSubmitting} />
      </CreateLayout>
    );
  }

  return (
    <CreateLayout
      key="edit"
      isSubmitting={false}
      handleSubmit={handlePreviewSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isErrorSnackbarOpen}
      handleCloseSnackbar={handleCloseErrorSnackbar}
      snackbarError={snackbarError}
      cancelButtonLabel={TASK_FORM_LABELS.BUTTON_CANCEL}
      submitButtonLabel={TASK_FORM_LABELS.BUTTON_PREVIEW}
      Header={FormPageHeader}
      headerClassName="mb-5 py-2"
    >
      <FormBuilder
        formState={formState}
        errors={errors}
        isSubmitting={isSubmitting}
        setName={setName}
        setDescription={setDescription}
        addSection={addSection}
        updateSection={updateSection}
        removeSection={removeSection}
        reorderSections={reorderSections}
        sectionDragIndexRef={sectionDragIndexRef}
        formId={formId}
      />
    </CreateLayout>
  );
};

export default EditForm;
